/**
 * PATH: packages/ui/src/contextMenu/ContextMenuPrimitives.tsx
 * FILE_ID: UI-CTXMENU-POSITIONED-PRIMITIVES-001
 * PURPOSE: The small stateless building blocks composed inside
 *          <ContextMenu>: section header, separator, and the plain
 *          clickable row. Split out of PositionedContextMenu.tsx
 *          (change #269 D4 500-line ceiling split).
 * ROLE IN ARCHITECTURE: Presentational leaves; no state, no portals.
 * MAIN EXPORTS: ContextMenuHeader, ContextMenuSeparator, ContextMenuItem,
 *          ContextMenuItemProps.
 * NON-RESPONSIBILITIES: Root panel geometry (ContextMenuRoot.tsx);
 *          submenu placement (ContextMenuSub.tsx).
 */
import type { ReactNode } from 'react';

import { cn } from '../lib/utils';
import { menuRowBaseCls, type ContextMenuIcon } from './positionedShared';

/* ─── Header ─── */

export function ContextMenuHeader({ children }: { children: ReactNode }) {
  return (
    <div
      role="presentation"
      className="px-3 py-1.5 text-[10px] font-semibold text-muted-foreground uppercase tracking-wide border-b border-border mb-1 pb-1 truncate max-w-[260px]"
    >
      {children}
    </div>
  );
}

/* ─── Separator ─── */

export function ContextMenuSeparator() {
  return <div role="separator" className="border-t border-border my-1" />;
}

/* ─── Item ─── */

export interface ContextMenuItemProps {
  icon?: ContextMenuIcon;
  onClick?: () => void;
  variant?: 'default' | 'danger';
  active?: boolean;
  disabled?: boolean;
  children: ReactNode;
  /** Optional colour swatch rendered as a leading dot (replaces icon). */
  dot?: string;
}

export function ContextMenuItem({
  icon: Icon,
  onClick,
  variant = 'default',
  active,
  disabled,
  children,
  dot,
}: ContextMenuItemProps) {
  const cls =
    variant === 'danger'
      ? cn(
          menuRowBaseCls,
          'hover:bg-red-500/10 text-red-600 dark:text-red-400',
          disabled && 'opacity-40 pointer-events-none'
        )
      : cn(
          menuRowBaseCls,
          'hover:bg-accent text-foreground',
          active && 'bg-accent font-semibold',
          disabled && 'opacity-40 pointer-events-none'
        );

  return (
    <button type="button" role="menuitem" className={cls} onClick={onClick} disabled={disabled}>
      {dot && <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: dot }} />}
      {Icon && !dot && <Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
      <span className="flex-1 truncate">{children}</span>
    </button>
  );
}
